import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import './SalesOverview.css';

function SalesOverview() {
  const { t } = useTranslation('sales');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    customers: { total: 0, active: 0, new_last_30_days: 0, with_quotations: 0 },
    quotations: { total: 0, draft: 0, pending: 0, approved: 0, accepted: 0, converted: 0, created_last_30_days: 0, created_last_7_days: 0 },
    revenue: { total_value: 0, avg_value: 0, accepted_value: 0, pending_value: 0, value_last_30_days: 0, value_last_7_days: 0 },
    conversions: { converted_count: 0, conversion_rate: 0 },
    top_customers: []
  });
  const [salesOrderMetrics, setSalesOrderMetrics] = useState({
    totalOrders: 0,
    totalValue: 0,
    avgOrderValue: 0,
    confirmedOrders: 0,
    statusBreakdown: {},
    monthlyTrends: []
  });
  const [invoiceMetrics, setInvoiceMetrics] = useState({
    total_metrics: {
      total_invoices: 0,
      total_value: 0,
      avg_invoice_value: 0,
      total_paid: 0,
      total_outstanding: 0
    },
    period_metrics: {
      period_invoices: 0,
      period_value: 0,
      period_paid: 0,
      period_outstanding: 0
    },
    overdue_info: {
      overdue_count: 0,
      overdue_amount: 0
    },
    recent_activity: {
      invoices_last_7_days: 0,
      value_last_7_days: 0
    },
    status_breakdown: [],
    aging_analysis: []
  });

  // Fetch sales metrics
  useEffect(() => {
    fetchSalesMetrics();
    fetchSalesOrderMetrics();
    fetchInvoiceMetrics();

    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchSalesMetrics();
      fetchSalesOrderMetrics();
      fetchInvoiceMetrics();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchSalesMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/sales/metrics/overview'));
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching sales metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesOrderMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/sales-orders/reports/dashboard'));
      if (response.ok) {
        const data = await response.json();
        setSalesOrderMetrics({
          totalOrders: data.status_metrics?.reduce((sum, s) => sum + s.order_count, 0) || 0,
          totalValue: data.status_metrics?.reduce((sum, s) => sum + (s.total_value || 0), 0) || 0,
          avgOrderValue: data.monthly_trends?.[0]?.avg_order_value || 0,
          confirmedOrders: data.status_metrics?.find(s => s.status === 'CONFIRMED')?.order_count || 0,
          statusBreakdown: data.status_metrics?.reduce((acc, s) => {
            acc[s.status] = s.order_count;
            return acc;
          }, {}) || {},
          monthlyTrends: data.monthly_trends || []
        });
      }
    } catch (error) {
      console.error('Error fetching sales order metrics:', error);
    }
  };

  const fetchInvoiceMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/invoices/metrics/summary'));
      if (response.ok) {
        const data = await response.json();
        setInvoiceMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching invoice metrics:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="sales-overview">
      {/* Primary Revenue Metrics - Most Important KPIs */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '2rem' }}>
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">💰</span>
            <span className="kpi-label">{t('dashboard.cards.totalRevenueValue')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : formatCurrency(metrics.revenue.total_value)}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.fromQuotations', { count: metrics.quotations.total })}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">✅</span>
            <span className="kpi-label">{t('dashboard.cards.paymentsReceived')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : formatCurrency(invoiceMetrics.total_metrics.total_paid)}</div>
          <div className="kpi-footer">
            <div className="kpi-change">
              {t('dashboard.labels.invoicesCount', { count: invoiceMetrics.total_metrics.total_invoices })}
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">⚠️</span>
            <span className="kpi-label">{t('dashboard.cards.outstanding')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : formatCurrency(invoiceMetrics.total_metrics.total_outstanding)}</div>
          <div className="kpi-footer">
            <div className="kpi-change">
              {t('dashboard.labels.overdueCount', { count: invoiceMetrics.overdue_info.overdue_count })}
            </div>
          </div>
        </div>
      </div>

      {/* Two-Column Layout for Activity Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>

        {/* Left Column - Recent Activity */}
        <div>
          <div className="section-header" style={{ marginBottom: '1rem' }}>
            <span className="section-icon">📈</span>
            <h3>{t('dashboard.sections.recentActivity')}</h3>
          </div>

          <div className="kpi-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="kpi-card">
              <div className="kpi-header">
                <span className="kpi-icon">📋</span>
                <span className="kpi-label">{t('dashboard.cards.newQuotations')}</span>
              </div>
              <div className="kpi-value">{loading ? '...' : metrics.quotations.created_last_7_days}</div>
              <div className="kpi-footer">
                <div className="kpi-change">{t('dashboard.labels.thisWeek')}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-header">
                <span className="kpi-icon">🔄</span>
                <span className="kpi-label">{t('dashboard.cards.conversionRate')}</span>
              </div>
              <div className="kpi-value">{loading ? '...' : `${metrics.conversions.conversion_rate}%`}</div>
              <div className="kpi-footer">
                <div className="kpi-change">{t('dashboard.labels.converted', { count: metrics.conversions.converted_count })}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-header">
                <span className="kpi-icon">📦</span>
                <span className="kpi-label">{t('dashboard.cards.salesOrders')}</span>
              </div>
              <div className="kpi-value">{loading ? '...' : salesOrderMetrics.totalOrders}</div>
              <div className="kpi-footer">
                <div className="kpi-change">
                  {t('dashboard.labels.confirmed', { count: salesOrderMetrics.confirmedOrders })}
                </div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-header">
                <span className="kpi-icon">👥</span>
                <span className="kpi-label">{t('dashboard.cards.activeCustomers')}</span>
              </div>
              <div className="kpi-value">{loading ? '...' : metrics.customers.active}</div>
              <div className="kpi-footer">
                <div className="kpi-change">{t('dashboard.labels.newThisMonth', { count: metrics.customers.new_last_30_days })}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Critical Issues */}
        <div>
          <div className="section-header" style={{ marginBottom: '1rem' }}>
            <span className="section-icon">⚡</span>
            <h3>{t('dashboard.sections.requiresAttention')}</h3>
          </div>

          {/* Overdue Invoices Alert */}
          {invoiceMetrics.overdue_info.overdue_count > 0 && (
            <div className="kpi-card" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)', borderColor: 'var(--danger)', marginBottom: '1rem' }}>
              <div className="kpi-header">
                <span className="kpi-icon">🚨</span>
                <span className="kpi-label">{t('dashboard.cards.overdueInvoices')}</span>
              </div>
              <div className="kpi-value" style={{ color: 'var(--danger)' }}>
                {invoiceMetrics.overdue_info.overdue_count}
              </div>
              <div className="kpi-footer">
                <div className="kpi-change">
                  {formatCurrency(invoiceMetrics.overdue_info.overdue_amount)} {t('dashboard.labels.overdue')}
                </div>
              </div>
            </div>
          )}

          {/* Pipeline Status */}
          <div className="kpi-card">
            <div className="kpi-header">
              <span className="kpi-icon">⏳</span>
              <span className="kpi-label">{t('dashboard.cards.pipelineValue')}</span>
            </div>
            <div className="kpi-value">{loading ? '...' : formatCurrency(metrics.revenue.pending_value)}</div>
            <div className="kpi-footer">
              <div className="kpi-change">{t('dashboard.labels.pendingApproval', { count: metrics.quotations.pending + metrics.quotations.approved })}</div>
            </div>
          </div>

          {/* This Month Performance */}
          <div className="kpi-card">
            <div className="kpi-header">
              <span className="kpi-icon">📊</span>
              <span className="kpi-label">{t('dashboard.cards.thisMonth')}</span>
            </div>
            <div className="kpi-value">{loading ? '...' : formatCurrency(metrics.revenue.value_last_30_days)}</div>
            <div className="kpi-footer">
              <div className="kpi-change">{t('dashboard.labels.quotationsCreated', { count: metrics.quotations.created_last_30_days })}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Critical Financial Analysis - Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>

        {/* Aging Analysis - Critical for Cash Flow */}
        {!loading && invoiceMetrics.aging_analysis.length > 0 && (
          <div className="customers-analytics">
            <div className="section-header">
              <span className="section-icon">⏰</span>
              <h3>{t('dashboard.sections.receivablesAging')}</h3>
            </div>
            <div className="customers-table">
              {invoiceMetrics.aging_analysis.filter(bucket => bucket.invoice_count > 0).map((bucket, index) => (
                <div key={index} className="customer-row">
                  <div className="customer-details">
                    <span className="customer-name">{bucket.aging_bucket === 'current' ? t('dashboard.aging.current') :
                      bucket.aging_bucket === '1-30' ? t('dashboard.aging.days1to30') :
                      bucket.aging_bucket === '31-60' ? t('dashboard.aging.days31to60') :
                      bucket.aging_bucket === '61-90' ? t('dashboard.aging.days61to90') :
                      t('dashboard.aging.over90Days')}</span>
                  </div>
                  <div className="orders-count">{bucket.invoice_count}</div>
                  <div className="revenue-amount">{formatCurrency(bucket.outstanding_amount)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Customers */}
        {!loading && metrics.top_customers.length > 0 && (
          <div className="customers-analytics">
            <div className="section-header">
              <span className="section-icon">🏆</span>
              <h3>{t('dashboard.sections.topCustomers')}</h3>
            </div>
            <div className="customers-table">
              {metrics.top_customers.slice(0, 5).map((customer) => (
                <div key={customer.customer_name} className="customer-row">
                  <div className="customer-details">
                    <span className="customer-name">{customer.customer_name}</span>
                  </div>
                  <div className="orders-count">{t('dashboard.labels.quotesCount', { count: customer.quotation_count })}</div>
                  <div className="revenue-amount">{formatCurrency(customer.total_value)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesOverview;
